import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ReportData } from "../../entry/server/app.ts";
import { appRepo } from "../../repo/app-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";

type ReportStatusFilter = "all" | "resolved" | "not_resolved";

const statusFilters: { value: ReportStatusFilter; label: string }[] = [
  { value: "all", label: "همه" },
  { value: "not_resolved", label: "حل‌نشده" },
  { value: "resolved", label: "حل‌شده" },
];

export default function ReportsScreen() {
  const [reports, setReports] = useState<ReportData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ReportStatusFilter>("all");
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [reasonPreview, setReasonPreview] = useState<{ title: string; reason: string } | null>(null);
  const { setMessage } = useContext(AppContext);

  const apiStatus = statusFilter === "all" ? undefined : statusFilter;

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await appRepo.getReports(page, apiStatus);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "دریافت لیست گزارشات ناموفق بود." });
        setReports([]);
        return;
      }
      setReports(response.data);
      setPagination(response.pagination ?? null);
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const onResolve = async (id: number) => {
    if (actionLoading !== null) return;
    if (!window.confirm("این گزارش به‌عنوان حل‌شده علامت‌گذاری شود؟")) return;

    setActionLoading(id);
    try {
      const res = await appRepo.resolveReport(id);
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "ثبت حل گزارش ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "گزارش به‌عنوان حل‌شده ثبت شد." });
      await fetchReports();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setActionLoading(null);
    }
  };

  const onFilterChange = (value: ReportStatusFilter) => {
    setStatusFilter(value);
    setPage(1);
  };

  return (
    <section className="space-y-4">
      <AdminPageTitle title="گزارشات" />

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <p className="mb-2 text-xs text-slate-400">فیلتر وضعیت</p>
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => onFilterChange(item.value)}
              className={`rounded-full px-3 py-1.5 text-xs transition ${
                statusFilter === item.value
                  ? "bg-indigo-500 text-white"
                  : "border border-slate-700 text-slate-300 hover:border-indigo-400 hover:text-indigo-300"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست گزارشات...
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="min-w-full divide-y divide-slate-800 text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عنوان</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">فیلم</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">کاربر</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">دلیل</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">وضعیت</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">تاریخ</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {reports.map((report) => (
                  <tr key={report.id} className="transition hover:bg-slate-900">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{report.id}</td>
                    <td className="max-w-[12rem] px-4 py-3 text-slate-100">{report.title || "—"}</td>
                    <td className="px-4 py-3">
                      {report.movie_id ? (
                        <Link
                          to={`/admin/movies/${report.movie_id}`}
                          target="_blank"
                          className="text-indigo-300 transition hover:text-indigo-200"
                        >
                          {report.movie_title || `فیلم #${report.movie_id}`}
                        </Link>
                      ) : (
                        <span className="text-slate-400">{report.movie_title || "—"}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                      <Link to={`/admin/users/${report.user_id}`} target="_blank" className="text-blue-500 transition hover:text-blue-400">
                        {report.username || "—"}
                      </Link>
                    </td>
                    <td className="max-w-[14rem] px-4 py-3 text-slate-400">
                      {report.reason ? (
                        <button
                          type="button"
                          onClick={() =>
                            setReasonPreview({
                              title: report.title || `گزارش #${report.id}`,
                              reason: report.reason,
                            })
                          }
                          className="block max-w-full truncate text-right text-indigo-300 transition hover:text-indigo-200"
                        >
                          مشاهده دلیل
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          report.is_resolved
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-amber-500/20 text-amber-300"
                        }`}
                      >
                        {report.is_resolved ? "حل‌شده" : "حل‌نشده"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{report.created_at || "—"}</td>
                    <td className="px-4 py-3">
                      {!report.is_resolved ? (
                        <button
                          type="button"
                          disabled={actionLoading === report.id}
                          onClick={() => void onResolve(report.id)}
                          className="rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500 hover:text-indigo-300 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {actionLoading === report.id ? "در حال ثبت…" : "علامت‌گذاری حل‌شده"}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!reports.length && (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-400">
                      گزارشی با فیلتر انتخاب‌شده یافت نشد.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}

      {reasonPreview ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reason-modal-title"
        >
          <button
            type="button"
            aria-label="بستن"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setReasonPreview(null)}
          />
          <div className="relative z-10 w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
            <div className="mb-3 flex items-start justify-between gap-3">
              <h2 id="reason-modal-title" className="text-sm font-semibold text-slate-100">
                دلیل گزارش
              </h2>
              <button
                type="button"
                onClick={() => setReasonPreview(null)}
                className="shrink-0 rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-300 transition hover:border-slate-500 hover:text-white"
              >
                بستن
              </button>
            </div>
            {reasonPreview.title ? (
              <p className="mb-3 text-xs text-slate-500">{reasonPreview.title}</p>
            ) : null}
            <p className="max-h-[min(60vh,24rem)] overflow-y-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-200">
              {reasonPreview.reason}
            </p>
          </div>
        </div>
      ) : null}
    </section>
  );
}
